import React from "react";
import { formatDate } from "../../utils/formatDate ";
import { useTranslation } from "react-i18next";
import Iterator from "../../utils/Iterator";
import { IMG_URl } from "../../../common/img_url";
import Visibility from "../../utils/Visibility";
import {
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../ui/sheet";
import { Skeleton } from "../../ui/skeleton";
import { OrderModel } from "../../../Models/OrderModel";
import DirhamIcon from "../../DirhamIcon/DirhamIcon";
import UAEDirhamIcon from "../../utils/UAEDirhamIcon";
import { getStatusColor } from "./OrderElement";

const OrderDetiles = ({
  order,
  t,
  name,
  loading,
}: {
  order: OrderModel;
  t: any;
  name: string;
  loading: boolean;
}) => {
  const formattedDate = formatDate(order?.created_at);

  return (
    <div>
      <SheetContent
        side={"bottom"}
        className="flex p-0 flex-col bg-background max-h-[70vh] overflow-hidden z-50"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border bg-gradient-to-r from-primary/5 via-primary/3 to-primary/5">
          <h2 className="text-2xl font-bold text-foreground mb-3">
            {t("gloabl.orderDetails")} #{order.num_order}
          </h2>
          <div
            className={`inline-block px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-semibold border-2 ${getStatusColor(
              order.status
            )}`}
          >
            {t(`orderStatus.${order.status}`)}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <Visibility visible={loading}>
            <DetilesLoading />
          </Visibility>
          <Visibility visible={!loading}>
            <div className="flex flex-col gap-4 md:gap-5">
              {/* Order Info */}
              <div className="flex flex-col gap-3 p-4 bg-card rounded-lg border border-border/50">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">{t("gloabl.total")}:</span>
                  <span dir="ltr" className="flex items-center gap-1 text-lg font-bold text-primary">
                    <UAEDirhamIcon className="w-4 h-4 md:w-5 md:h-5" />
                    {order.price}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-muted-foreground">{t("gloabl.date")}:</span>
                  <span className="text-foreground">{formattedDate}</span>
                </div>
                <Visibility visible={!!order.note}>
                  <div className="pt-2 border-t border-border/50">
                    <span className="font-semibold text-muted-foreground">{t("gloabl.Notes")}:</span>
                    <p className="text-foreground mt-1">{order.note || t("gloabl.noNotes")}</p>
                  </div>
                </Visibility>
                <div className="pt-2 border-t border-border/50 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-muted-foreground">{t("gloabl.name")}:</span>
                    <span className="text-foreground">{order.customer_name || "___"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-muted-foreground">{t("contact.phone")}:</span>
                    <span dir="ltr" className="text-foreground">{order.customer_phone || "___"}</span>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="text-lg md:text-xl font-bold text-foreground mb-3">
                  {t("gloabl.items")}
                </h3>
                <div className="flex flex-col gap-3 md:gap-4">
                  <Iterator
                    data={order.orderProdeuct}
                    render={(item: any, i: number) => (
                      <div
                        key={i}
                        className="opacity-0 animate-fadeInUp"
                        style={{
                          animationDelay: `${i * 50}ms`,
                        }}
                      >
                        <ItemStructer item={item} name={name} />
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </Visibility>
        </div>
      </SheetContent>
    </div>
  );
};

export default OrderDetiles;

const ItemStructer = ({ item, name }: { item: any; name: string }) => {
  return (
    <div className="flex items-start gap-3 md:gap-4 p-3 md:p-4 bg-card rounded-xl border border-border/50 shadow-sm">
      <img
        src={`${IMG_URl}${item.product.image}`}
        alt="img"
        className="object-cover rounded-xl w-20 h-20 md:w-24 md:h-24 flex-shrink-0"
      />

      <div className="flex flex-col gap-2 flex-1 min-w-0">
        <h3 className="text-base md:text-lg font-bold text-foreground">
          {item.product[name]}
        </h3>
        <Visibility
          visible={item.product.option && item.product.option.length > 0}
        >
          <div className="text-xs md:text-sm text-muted-foreground space-y-1">
            <Iterator
              data={item.product.option}
              render={(e: any, i: number) => renderOptions(e, i, name)}
            />
          </div>
        </Visibility>

        <div className="flex items-center text-foreground gap-2 text-sm md:text-base font-semibold">
          <span>{item.price}</span>
          <span>x</span>
          <span>{item.amount}</span>
          <span>=</span>
          <span className="flex items-center gap-1 text-primary">
            <UAEDirhamIcon className="w-3 h-3 md:w-4 md:h-4" />
            {item.price * item.amount}
          </span>
        </div>

        <Visibility visible={!!item.note}>
          <p className="text-xs md:text-sm text-muted-foreground italic">
            {item.note}
          </p>
        </Visibility>
      </div>
    </div>
  );
};
const renderOptions = (e: any, i: number, name: string) => {
  return (
    <div key={i} className="text-xs md:text-sm text-muted-foreground">
      <span className="font-semibold text-foreground">{e[name]}:</span>
      <span className="ml-1">{e.sub_option[name]}</span>
      <Visibility visible={e.sub_option.price > 0}>
        <span dir="ltr" className="flex items-center gap-1 ml-1 text-primary">
          (+
          <UAEDirhamIcon className="w-3 h-3" />
          {e.sub_option.price})
        </span>
      </Visibility>
    </div>
  );
};

const DetilesLoading = () => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-3 p-4 bg-card rounded-lg border border-border/50">
      <Skeleton className="w-48 h-6" />
      <Skeleton className="w-40 h-4" />
      <Skeleton className="w-44 h-4" />
    </div>
    <div className="flex flex-col gap-3">
      <Skeleton className="w-32 h-6" />
      <div className="flex gap-3 p-4 bg-card rounded-xl border border-border/50">
        <Skeleton className="w-20 h-20 rounded-xl flex-shrink-0" />
        <div className="flex flex-col gap-2 flex-1">
          <Skeleton className="w-48 h-5" />
          <Skeleton className="w-32 h-4" />
          <Skeleton className="w-40 h-4" />
        </div>
      </div>
    </div>
  </div>
);
